import { useState } from 'react';
import { Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';

import MainCard from 'components/MainCard';
import useApi from 'hooks/useApi';
import LegalDocumentDialog from 'components/legal/LegalDocumentDialog';
import { formatLegalDate, unwrapEnvelope } from 'components/legal/legalUtils';
import {
  LEGAL_DOCUMENT_LABELS,
  LEGAL_DOCUMENT_TYPES,
  LEGAL_SLUGS,
  LegalDocument,
  LegalDocumentMeta,
  LegalDocumentSlot,
  LegalDocumentType,
  PublishLegalDocumentPayload
} from 'types/legal';
import DocumentHistory from './DocumentHistory';
import LegalDocumentEditor from './LegalDocumentEditor';

interface LegalDocumentsCardProps {
  clientUid: string;
  clientName?: string;
  slots: Partial<Record<LegalDocumentType, LegalDocumentSlot>>;
  onPublished: () => void;
}

const publishedBy = (meta: LegalDocumentMeta): string => {
  const by = typeof meta.createdBy === 'string' ? '' : meta.createdBy?.name;
  const on = formatLegalDate(meta.createdAt);
  if (by && on) return ` · published by ${by} on ${on}`;
  if (on) return ` · published on ${on}`;
  return '';
};

// A client's own Privacy Policy and Terms. There are no platform defaults: a client
// that hasn't published a document has none, and the Privacy/Terms checkbox can't be
// switched on until both exist.
const LegalDocumentsCard = ({ clientUid, clientName, slots, onPublished }: LegalDocumentsCardProps) => {
  const { getClientLegalDocumentVersion, publishClientLegalDocument } = useApi();
  const [viewing, setViewing] = useState<{ type: LegalDocumentType; meta: LegalDocumentMeta } | null>(null);
  const [historyType, setHistoryType] = useState<LegalDocumentType | null>(null);
  const [editingType, setEditingType] = useState<LegalDocumentType | null>(null);

  const loadVersion = (type: LegalDocumentType, meta: LegalDocumentMeta): Promise<LegalDocument> =>
    getClientLegalDocumentVersion(clientUid, LEGAL_SLUGS[type], meta.version).then((response: any) =>
      unwrapEnvelope<LegalDocument>(response)
    );

  const publish = async (type: LegalDocumentType, payload: PublishLegalDocumentPayload) => {
    unwrapEnvelope(await publishClientLegalDocument(clientUid, LEGAL_SLUGS[type], payload));
  };

  const renderRow = (type: LegalDocumentType) => {
    const slot: LegalDocumentSlot = slots[type] ?? { current: null, versions: [] };
    const current = slot.current;
    const versions = slot.versions ?? [];

    let meta: string;
    if (!current) {
      meta = 'Nothing published yet.';
    } else {
      const effective = formatLegalDate(current.effectiveDate);
      meta = `Version ${current.version}${effective ? ` · effective ${effective}` : ''}${publishedBy(current)}`;
    }

    return (
      <Stack
        key={type}
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        sx={{ py: 2 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1">{LEGAL_DOCUMENT_LABELS[type]}</Typography>
            {current ? (
              <Chip size="small" label="Published" color="success" variant="outlined" />
            ) : (
              <Chip size="small" label="Not published" color="error" variant="outlined" />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {meta}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexShrink={0} flexWrap="wrap" useFlexGap>
          {current && (
            <Button variant="outlined" color="secondary" size="small" onClick={() => setViewing({ type, meta: current })}>
              View
            </Button>
          )}
          {versions.length > 0 && (
            <Button variant="outlined" color="secondary" size="small" onClick={() => setHistoryType(type)}>
              History
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            onClick={() => setEditingType(type)}
            sx={{ bgcolor: 'secondary.dark', '&:hover': { bgcolor: 'secondary.main' } }}
          >
            {current ? 'Edit' : 'Publish first version'}
          </Button>
        </Stack>
      </Stack>
    );
  };

  const editingSlot = editingType ? slots[editingType] : undefined;
  const editingLatest = editingSlot?.current?.version ?? 0;
  const historySlot = historyType ? slots[historyType] : undefined;

  return (
    <>
      <MainCard title="Documents" subheader="Saving publishes a new version. People who already accepted are not asked again.">
        {LEGAL_DOCUMENT_TYPES.map((type, index) => (
          <Box key={type}>
            {index > 0 && <Divider />}
            {renderRow(type)}
          </Box>
        ))}
      </MainCard>

      {viewing && (
        <LegalDocumentDialog
          open
          slug={LEGAL_SLUGS[viewing.type]}
          load={() => loadVersion(viewing.type, viewing.meta)}
          showOpenInNewTab={false}
          onClose={() => setViewing(null)}
        />
      )}

      {historyType && historySlot && (
        <DocumentHistory
          open
          type={historyType}
          versions={historySlot.versions ?? []}
          currentVersion={historySlot.current ? historySlot.current.version : null}
          loadVersion={(meta) => loadVersion(historyType, meta)}
          onClose={() => setHistoryType(null)}
        />
      )}

      {editingType && (
        <LegalDocumentEditor
          open
          type={editingType}
          contextLabel={clientName || 'This client'}
          currentVersion={editingLatest || null}
          nextVersion={editingLatest + 1}
          loadInitial={editingSlot?.current ? () => loadVersion(editingType, editingSlot.current as LegalDocumentMeta) : undefined}
          publish={(payload) => publish(editingType, payload)}
          onPublished={onPublished}
          onClose={() => setEditingType(null)}
        />
      )}
    </>
  );
};

export default LegalDocumentsCard;
